import { useMemo } from "react";
import { toast } from "sonner";
import { ScanLine, Search, MapPin, GlassWater, Receipt, History, Wine, LayoutGrid, Package, ArrowLeftRight, Settings } from "lucide-react";
import QRCode from "qrcode";
import { GrotalisLogo, GrotalisMark } from "@/components/brand/Logo";
import { SectionHead } from "@/components/brand/BrandSections";

const STATS = [["Total bottles", "1,428", "+24 this month"], ["Collection value", "€284,500", "Purchase value"], ["Bottles consumed", "62", "Last 12 months"], ["Bottles sold", "118", "€41,900 in sales"]];
const CATS = [["Red wine", 54], ["Champagne", 16], ["Port", 11], ["White wine", 9], ["Madeira", 5], ["Whisky & spirits", 5]];
const LOCS = [["Vault I · Arch 1", 412], ["Vault I · Arch 2", 386], ["Rack B · Shelf 3", 240], ["Wooden cases", 390]];
const MOVES = [["Château Margaux 2015", "Vault I · Arch 1", "Moved", "Today 10:42"], ["Taylor's Vintage Port 1977", "Rack B · Shelf 3", "Consumed", "Yesterday"], ["Krug Clos du Mesnil 2006", "Wooden cases", "Sold", "2 Nov"], ["Blandy's Malmsey 1969", "Vault I · Arch 2", "Added", "1 Nov"]];
const NAV = [[LayoutGrid, "Collection"], [MapPin, "Cellar locations"], [ScanLine, "Scan"], [ArrowLeftRight, "Bottle history"], [Receipt, "Sales"], [Package, "Boxes"], [Settings, "Settings"]];

const Sidebar = () => (
  <aside className="hidden md:flex w-52 shrink-0 flex-col p-5" style={{ background: "var(--g-obsidian)" }}>
    <GrotalisLogo size={26} />
    <div className="mt-8 space-y-1">
      {NAV.map(([I, l], i) => (
        <span key={l} className="flex items-center gap-2.5 rounded-md px-3 py-2 text-sm" style={{ color: i === 0 ? "var(--g-ivory)" : "var(--g-stone)", background: i === 0 ? "var(--g-forest)" : "transparent", borderLeft: i === 0 ? "2px solid var(--g-gold)" : "2px solid transparent" }}><I className="w-4 h-4" />{l}</span>
      ))}
    </div>
    <p className="mt-auto text-xs" style={{ color: "var(--g-stone)" }}>Private collection · Lisbon</p>
  </aside>
);

export const DashboardMock = () => (
  <section id="dashboard" className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="brand-dashboard">
    <SectionHead n={3} title="Desktop dashboard" text="The collection at a glance: ivory canvas, white cards, charcoal text, gold only for the value that matters." />
    <div className="rounded-2xl overflow-hidden border flex shadow-[0_30px_80px_-40px_rgba(23,28,26,.45)]" style={{ borderColor: "var(--g-border)" }}>
      <Sidebar />
      <div className="flex-1 p-6 sm:p-8 min-w-0" style={{ background: "var(--g-ivory)" }}>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div><p className="text-xs" style={{ color: "#6E6F69" }}>Good morning, Olivier</p><h3 className="g-serif text-3xl font-medium" style={{ color: "var(--g-obsidian)" }}>Your private collection</h3></div>
          <div className="flex gap-2"><button className="g-btn g-btn-secondary"><Search className="w-4 h-4" />Search collection</button><button className="g-btn g-btn-primary"><ScanLine className="w-4 h-4" />Scan QR code</button></div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {STATS.map(([l, v, s], i) => (
            <div key={l} className="g-card p-4" data-testid={`mock-stat-${i}`}>
              <p className="text-xs" style={{ color: "#6E6F69" }}>{l}</p>
              <p className="text-2xl font-semibold mt-1 tabular-nums" style={{ color: i === 1 ? "#8A6F3E" : "var(--g-obsidian)" }}>{v}</p>
              <p className="text-xs mt-1" style={{ color: "#6E6F69" }}>{s}</p>
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-3 mt-3">
          <div className="g-card p-5">
            <p className="text-sm font-semibold mb-4">Wine & spirits categories</p>
            {CATS.map(([c, p]) => (
              <div key={c} className="mb-2.5"><div className="flex justify-between text-xs mb-1"><span>{c}</span><span className="tabular-nums" style={{ color: "#6E6F69" }}>{p}%</span></div>
                <div className="h-1.5 rounded-full" style={{ background: "#EDE8DC" }}><div className="h-full rounded-full" style={{ width: `${p}%`, background: "var(--g-forest)" }} /></div></div>
            ))}
          </div>
          <div className="g-card p-5">
            <p className="text-sm font-semibold mb-4">Cellar locations</p>
            <div className="grid grid-cols-2 gap-2">
              {LOCS.map(([l, n]) => <div key={l} className="rounded-lg border p-3" style={{ borderColor: "var(--g-border)" }}><MapPin className="w-4 h-4" style={{ color: "var(--g-gold)" }} /><p className="text-sm mt-2">{l}</p><p className="text-xs tabular-nums" style={{ color: "#6E6F69" }}>{n} bottles</p></div>)}
            </div>
          </div>
        </div>
        <div className="g-card mt-3 overflow-x-auto">
          <p className="text-sm font-semibold px-5 pt-5 pb-3">Recent movements</p>
          <table className="w-full text-sm">
            <tbody>
              {MOVES.map(([w, l, a, d]) => (
                <tr key={w} className="border-t" style={{ borderColor: "var(--g-border)" }}>
                  <td className="px-5 py-3 g-serif text-base" style={{ color: "var(--g-obsidian)" }}>{w}</td>
                  <td className="px-5 py-3" style={{ color: "#5C5E58" }}>{l}</td>
                  <td className="px-5 py-3"><span className="text-xs rounded-full px-2 py-0.5" style={{ background: a === "Sold" ? "#EAF1EC" : a === "Consumed" ? "#F3ECE0" : "#EEF1F2", color: a === "Sold" ? "#3D765B" : a === "Consumed" ? "#8A6224" : "#486B7C" }}>{a}</span></td>
                  <td className="px-5 py-3 text-xs whitespace-nowrap" style={{ color: "#6E6F69" }}>{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
);

const ACTIONS = [[ScanLine, "Scan QR code"], [Search, "Search collection"], [MapPin, "Change bottle location"], [GlassWater, "Register consumption"], [Receipt, "Register sale"], [History, "View bottle history"]];

export const MobileMock = () => (
  <section id="mobile" style={{ background: "var(--g-forest)" }} data-testid="brand-mobile">
    <div className="max-w-6xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-12 items-center">
      <SectionHead n={4} dark title="Mobile — one tap to the bottle" text="Scanning comes first. The six everyday actions sit on the first screen; the brand stays quiet so the work stays fast." />
      <div className="mx-auto w-[300px] rounded-[42px] p-3" style={{ background: "#0E1210", boxShadow: "0 40px 80px -30px rgba(0,0,0,.6)" }}>
        <div className="rounded-[32px] overflow-hidden" style={{ background: "var(--g-ivory)" }}>
          <div className="px-5 pt-6 pb-5" style={{ background: "var(--g-obsidian)" }}>
            <div className="flex items-center justify-between"><GrotalisLogo size={22} showSub={false} /><span className="text-[11px]" style={{ color: "var(--g-stone)" }}>Pop & Taste</span></div>
            <button onClick={() => toast("Camera opens — point at the bottle's QR code")} className="mt-5 w-full rounded-xl py-4 flex items-center justify-center gap-2 text-sm font-medium" style={{ background: "var(--g-gold)", color: "var(--g-obsidian)" }} data-testid="mock-mobile-scan"><ScanLine className="w-5 h-5" />Scan QR code</button>
          </div>
          <div className="p-4 grid grid-cols-2 gap-2">
            {ACTIONS.slice(1).map(([I, l], i) => (
              <button key={l} onClick={() => toast(l)} className="g-card p-3 text-left" data-testid={`mock-mobile-action-${i}`}><I className="w-4 h-4" style={{ color: "var(--g-forest)" }} /><span className="block text-xs mt-2 font-medium leading-snug">{l}</span></button>
            ))}
          </div>
          <div className="px-4 pb-5">
            <p className="text-[11px] mb-2" style={{ color: "#6E6F69" }}>Last scanned</p>
            <div className="g-card p-3 flex items-center gap-3"><Wine className="w-4 h-4" style={{ color: "var(--g-burgundy)" }} /><span className="min-w-0"><span className="g-serif block text-sm truncate" style={{ color: "var(--g-obsidian)" }}>Château Margaux 2015</span><span className="block text-[11px]" style={{ color: "#6E6F69" }}>BTL-000214 · Vault I · Arch 1</span></span></div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const QrSvg = ({ text }) => {
  const d = useMemo(() => {
    const { modules } = QRCode.create(text, { errorCorrectionLevel: "M" });
    let p = "";
    for (let r = 0; r < modules.size; r += 1) for (let c = 0; c < modules.size; c += 1) if (modules.get(r, c)) p += `M${c} ${r}h1v1h-1z`;
    return { p, n: modules.size };
  }, [text]);
  return <svg viewBox={`0 0 ${d.n} ${d.n}`} shapeRendering="crispEdges" style={{ width: "20mm", height: "20mm" }}><path d={d.p} fill="#000" /></svg>;
};

export const LabelMock = () => (
  <section id="label" className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="brand-label">
    <SectionHead n={5} title="Thermal QR label" text="Black and white only, high contrast, no textures — the brand appears as a plain word. The QR code and bottle identifiers stay exactly as they are; the printed domain becomes grotalis.com." />
    <div className="flex flex-wrap items-end gap-10">
      <div>
        <div className="relative bg-white shadow-lg" style={{ width: "50mm", height: "29mm", fontFamily: "Inter, Arial, sans-serif", color: "#000" }} data-testid="mock-label">
          <div className="absolute" style={{ left: "3.5mm", top: "4.5mm" }}><QrSvg text="HTTPS://GROTALIS.COM/Q/BTL-000214" /></div>
          <div className="absolute flex flex-col justify-between" style={{ left: "26mm", top: "3.4mm", bottom: "3.4mm", right: "2mm" }}>
            <p style={{ fontSize: "7pt", fontWeight: 700, lineHeight: 1.1 }}>CHÂTEAU<br />MARGAUX</p>
            <p style={{ fontSize: "11pt", fontWeight: 700, lineHeight: 1 }}>2015</p>
            <p style={{ fontSize: "6pt", color: "#444", lineHeight: 1 }}>05/11/2026</p>
            <p style={{ fontSize: "7pt", fontWeight: 700, lineHeight: 1 }}>grotalis.com</p>
          </div>
        </div>
        <p className="text-xs mt-3" style={{ color: "#6E6F69" }}>50 × 29 mm · real size · Brother QL-700, 29 mm roll</p>
      </div>
      <div className="g-card p-5 max-w-sm text-sm" style={{ color: "#5C5E58" }}>
        <p className="font-semibold mb-2" style={{ color: "var(--g-obsidian)" }}>Labels already printed keep working</p>
        <p>Existing stickers point to thecellarbook.pt/Q/…; that address will keep redirecting to the same bottle on grotalis.com once the domain is moved.</p>
        <div className="mt-4 flex items-center gap-3"><GrotalisMark color="#000" size={28} bold /><span className="text-xs">Symbol not used on thermal labels — too fine at 300 dpi.</span></div>
      </div>
    </div>
  </section>
);
